import { Autocomplete, Icon, IconButton, Popper, TextField } from '@mui/material';
import { PaymentRowData, defaultPaymentRowData, usePaymentMethods } from 'hooks/payment';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldError } from 'react-hook-form';
import useDeepCompareEffect from 'use-deep-compare-effect';

export const REQUIRED_FIELDS = ['cardNumber', 'bank', 'amount'] as const;
export const FIELD_MAPPING = {
  cardNumber: 'account',
  bank: 'bank',
  amount: 'value',
};

export type PaymentRowProps = {
  number: number;
  value?: PaymentRowData;
  onChange: (value: PaymentRowData) => void;
  onRemove: (id: number) => void;
  error?: FieldError & {
    cardNumber?: FieldError;
    bank?: FieldError;
    amount?: FieldError;
    secondaryAccount?: FieldError;
  };
  onRequiredError: (params: { id: number; field: typeof REQUIRED_FIELDS[number] }) => void;
  onValueError: (params: { id: number; message: string }) => void;
  onPaste: (value: string) => void;
  onPasteAmount: (value: string) => void;
};

export const PaymentRow = ({
  number,
  value = defaultPaymentRowData,
  onChange,
  onRemove,
  error,
  onRequiredError,
  onValueError,
  onPaste,
  onPasteAmount,
}: PaymentRowProps) => {
  const [typedBankValue, setTypedBankValue] = useState('');
  const { methods } = usePaymentMethods({ query: typedBankValue, account: value.cardNumber });
  const { methods: allMethods } = usePaymentMethods();
  const [focused, setFocused] = useState(false);

  const selectedMethod = allMethods[value.bank];

  const autocompleteValue = useMemo(() => ({ id: value.bank, label: selectedMethod?.name ?? '' }), [
    value,
    selectedMethod,
  ]);

  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  const handleRequiredError = () => {
    const { id } = value;

    REQUIRED_FIELDS.forEach((field) => {
      if (!value[field]) {
        onRequiredError({ id, field });
      }
    });
  };

  const handleValueError = () => {
    let message = '';

    Object.values(FIELD_MAPPING).forEach((field) => {
      if (value.error?.[field] && Array.isArray(value.error[field])) {
        message = message.concat(value.error[field].join(' '), ' ');
      }

      if (value.error?.[field] && !Array.isArray(value.error[field])) {
        message = message.concat(value.error[field], ' ');
      }
    });

    if (value.error?.non_field_errors?.length) {
      message = message.concat(value.error.non_field_errors?.join(' '), ' ');
    }

    if (message) {
      onValueError({ id: value.id, message });
    }
  };

  const previousCardNumber = useRef('');
  const previousMethodsLength = useRef(0);

  useDeepCompareEffect(() => {
    const slugs = Object.keys(methods);

    previousCardNumber.current = value.cardNumber;
    previousMethodsLength.current = slugs.length;

    if (slugs.length > 1 && slugs[0] === 'bankpaymentmethod:203') {
      return;
    }

    if (slugs.length && value.cardNumber && !slugs.includes(value.bank) && !typedBankValue) {
      setTimeout(() => {
        onChange({
          ...value,
          bank: slugs[0],
        });
      }, 0);
    }
  }, [methods, value]);

  useEffect(() => {
    if (!error) {
      return;
    }

    handleRequiredError();
  }, [error]);

  useEffect(() => {
    handleValueError();
  }, [
    value?.error?.account,
    value?.error?.slug,
    value?.error?.value,
    value?.error?.secondary_account,
    value?.error?.non_field_errors,
  ]);

  return (
    <div className="mb-4">
      <div className="flex flex-row -ml-8">
        <div className="mr-4 text-xs w-10 mt-3">{number}</div>
        <TextField
          className="mr-2 w-64 basis-auto grow-1 shrink-0"
          error={!!value.error?.account || !!error?.cardNumber}
          focused={focused}
          placeholder="Номер карты или телефона"
          value={value.cardNumber}
          onBlur={onBlur}
          onChange={(e) => onChange({ ...value, cardNumber: e.target.value })}
          onFocus={onFocus}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text');

            if (text.includes('\n') || text.includes('\t')) {
              event.preventDefault();
              onPaste(text);
            }
          }}
        />
        <Autocomplete
          disableClearable
          PopperComponent={({ children, anchorEl, open }: any) => {
            return (
              <Popper anchorEl={anchorEl} open={open}>
                {children}
              </Popper>
            );
          }}
          className="relative mr-2 w-28 basis-auto grow-1 shrink-0"
          classes={{ popper: 'w-full' }}
          filterOptions={(options: any) => options}
          filterSelectedOptions={false}
          freeSolo={true}
          inputValue={typedBankValue || selectedMethod?.name}
          options={
            Object.values(methods).map((method) => ({
              label: method.name,
              id: method.slug,
            })) ?? []
          }
          renderInput={(params: any) => {
            return (
              <TextField
                error={!!value.error?.slug || !!error?.bank}
                focused={focused}
                placeholder="Банк"
                {...params}
                inputProps={{ ...params.inputProps, sx: { lineHeight: 'initial' } }}
              />
            );
          }}
          size="small"
          value={autocompleteValue}
          onBlur={onBlur}
          onChange={(e, newValue) => {
            if (newValue instanceof Object) {
              onChange({ ...value, bank: newValue?.id ?? '' });
              setTypedBankValue('');
            }
          }}
          onFocus={onFocus}
          onInputChange={(e, inputValue) => {
            if (!e) {
              return;
            }

            setTypedBankValue(inputValue);
          }}
        />
        <TextField
          className="mr-2 w-28 basis-auto grow-1 shrink-0"
          error={!!value.error?.value || !!error?.amount}
          focused={focused}
          placeholder="Сумма"
          value={value.amount || ''}
          onBlur={onBlur}
          onChange={(e) => onChange({ ...value, amount: parseFloat(e.target.value) })}
          onFocus={onFocus}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text');

            if (text.includes('\n') || text.includes('\t')) {
              event.preventDefault();
              onPasteAmount(text);
            }
          }}
        />
        <TextField
          focused={focused}
          placeholder="Комментарий"
          value={value.comment}
          onBlur={onBlur}
          onChange={(e) => onChange({ ...value, comment: e.target.value })}
          onFocus={onFocus}
        />
        <IconButton className="-mr-12 ml-2 mt-0 h-10 items-start" onClick={() => onRemove(value.id)}>
          <Icon>close</Icon>
        </IconButton>
      </div>
      {selectedMethod?.has_secondary_account && (
        <div className="mx-2 mt-2">
          <TextField
            className="w-full basis-auto grow-1 shrink-0"
            error={!!value.error?.secondary_account || !!error?.secondaryAccount}
            focused={focused}
            placeholder="Номер телефона"
            value={value.secondaryAccount}
            onBlur={onBlur}
            onChange={(e) => onChange({ ...value, secondaryAccount: e.target.value })}
            onFocus={onFocus}
          />
          <div className="text-xs mt-2">
            В {selectedMethod.name} платежи по № карты часто зависают. Просьба, по возможности, указывать номер
            телефона, привязанный к банку.
          </div>
        </div>
      )}
      {error?.message && <div className="text-xs mt-2 ml-2 text-red-600">{error.message}</div>}
    </div>
  );
};
